import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import { X, ArrowsLeftRight } from "@phosphor-icons/react";

const CFG = {
  installation: { base: "/installations", field: "target_installation_id", title: "Reasignar instalación", noun: "instalación" },
  obligation: { base: "/obligations", field: "target_obligation_id", title: "Reasignar documentos", noun: "obligación" },
};

export const ReassignDialog = ({ kind, sourceId, onClose, onDone }) => {
  const cfg = CFG[kind];
  const [info, setInfo] = useState(null);
  const [target, setTarget] = useState("");
  const [deleteSource, setDeleteSource] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    api.get(`${cfg.base}/${sourceId}/reassign-preview`)
      .then(r => setInfo(r.data))
      .catch(e => { toast.error(e?.response?.data?.detail || "Error"); onClose(); });
  }, [sourceId]); // eslint-disable-line

  const submit = async () => {
    if (!target) { toast.error(`Selecciona la ${cfg.noun} destino`); return; }
    setBusy(true);
    try {
      const r = await api.post(`${cfg.base}/${sourceId}/reassign`, { [cfg.field]: target, delete_source: deleteSource });
      const d = r.data;
      const moved = kind === "installation"
        ? `${d.moved_obligations} obligación(es) y ${d.moved_documents} documento(s)`
        : `${d.moved_documents} documento(s)`;
      toast.dismiss();
      toast.success(`Reasignados ${moved}${d.deleted_source ? " · origen eliminado" : ""}`);
      onDone(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="reassign-dialog">
      <div className="bg-white rounded-md w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><ArrowsLeftRight size={18} weight="bold" className="text-blue-600"/> {cfg.title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="reassign-close-btn"><X size={18}/></button>
        </div>
        {!info ? <div className="p-6 text-sm text-neutral-500">Cargando…</div> : (
          <div className="p-5 space-y-4">
            <div className="text-sm bg-neutral-50 border border-neutral-200 rounded-md p-3" data-testid="reassign-summary">
              <div className="font-semibold text-neutral-800">Origen: {info.source.name}</div>
              <div className="text-neutral-600 mt-1">
                {kind === "installation" && <>{info.obligations} obligación(es) · </>}{info.documents} documento(s) relacionados
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">{cfg.noun} destino (misma empresa)</label>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white text-sm" data-testid="reassign-target-select">
                <option value="">— Selecciona —</option>
                {info.targets.map(t => <option key={t.id} value={t.id}>{t.name}{t.authority ? ` · ${t.authority}` : ""}{t.code ? ` · ${t.code}` : ""}</option>)}
              </select>
              {kind === "obligation" && target && !info.targets.find(t => t.id === target)?.installation_id && (
                <p className="text-xs text-amber-700 mt-1" data-testid="reassign-no-installation-warning">La obligación destino es a nivel empresa: los documentos quedarán sin instalación asignada.</p>
              )}
              {info.targets.length === 0 && <p className="text-xs text-amber-700 mt-1" data-testid="reassign-no-targets">No hay otra {cfg.noun} en esta empresa.</p>}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={deleteSource} onChange={(e) => setDeleteSource(e.target.checked)} className="w-4 h-4 accent-rose-600" data-testid="reassign-delete-source"/>
              Eliminar la {cfg.noun} origen después de reasignar
            </label>
            <p className="text-[11px] text-neutral-500">La operación queda registrada en Auditoría.</p>
          </div>
        )}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-neutral-200">
          <button onClick={onClose} className="px-4 py-2 border border-neutral-300 rounded-md text-sm" data-testid="reassign-cancel-btn">Cancelar</button>
          <button onClick={submit} disabled={busy || !info} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-semibold disabled:opacity-50" data-testid="reassign-confirm-btn">
            {busy ? "Reasignando…" : "Reasignar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReassignDialog;
